/**
 * The error the API layer raises when it cannot obtain an access token.
 *
 * 🔴 **Why this type exists, and why it exists HERE first.** ptc-statetax is the ORIGIN of
 * the fleet's "conditional auth header, then send anyway" defect — `ptc-conversions`
 * inherited it by copying this repo's API layer, and its own file said so in a header
 * comment. It was found and fixed there first (PR #33); this module is that fix coming
 * home to the repo the pattern started in.
 *
 * Every one of the 11 call sites across `stateTaxClientsApi.ts` (8) and `complianceApi.ts`
 * (3) used to build requests the same way: ask for a token, attach the header only when one
 * came back, and issue the request either way. Written that way it reads as defensive. It is
 * the opposite — it converts *"we hold no credentials"* into *"send it anyway and let the
 * server refuse."*
 *
 * **What staff actually saw.** `AuthMiddleware` is fail-closed, so the tokenless request came
 * back `401 { error: "No authorization header" }`. `fetchApi` turned that into an `ApiError`,
 * react-query set `isError`, and the pages interpolate the server's raw string into their
 * load-failure copy — so the sales-tax client list rendered, verbatim:
 *
 *     Failed to load clients: No authorization header
 *
 * That sentence is a data-loading failure on its face and an authentication failure in fact.
 * It offers a person mid-workday nothing to act on: not what broke, not that signing in
 * again is the fix. On ptc-conversions the same class of message named the wrong system
 * outright and cost a full triage session against a Dataverse that was healthy the whole
 * time.
 *
 * The fix is to make "no token" a **thrown, typed, named** condition rather than a missing
 * header — so the request is never sent, and the UI can say what actually went wrong.
 *
 * ⚠ The defective expression is deliberately never written out verbatim in this repo's
 * source. `authError.test.ts` greps the real API modules for it, and prose quoting the old
 * shape would trip that guard. Reword the comment; never weaken the guard.
 */

/**
 * Why a token could not be obtained. A union rather than a free string so a new failure path
 * has to be named here — where the user-facing copy is decided — instead of arriving as an
 * unhandled variant that silently renders as a generic failure.
 *
 * These are exactly the paths `acquireApiToken` can take without producing a token. Adding a
 * path to that function without adding a member here will not compile.
 */
export type AuthFailureReason =
  /** No signed-in account: signed out, sessionStorage cleared, or the tab was reopened. */
  | 'no-account'
  /**
   * Silent acquisition needed a UI and MSAL has begun a full-page redirect. The page is
   * navigating away, so the request must be abandoned — its response could only ever be
   * discarded, and firing it races a doomed 401 against the navigation.
   */
  | 'interaction-required'
  /**
   * An interactive redirect already completed within the retry window and silent acquisition
   * STILL fails. That rules out an expiry — a fresh interactive auth just happened — so
   * redirecting again would loop. Distinct from `acquisition-failed` because the remedy
   * differs: signing in again is what just failed.
   */
  | 'interaction-did-not-help'
  /**
   * MSAL's `interaction.status` flag was stale from an interrupted redirect. The cache has
   * been cleared and a clean login started; this request dies so that one can proceed.
   */
  | 'stale-interaction-cleared'
  /** Anything else — a network blip, an AAD hiccup, a consent problem. */
  | 'acquisition-failed';

/**
 * No access token was available, so **no request was sent**.
 *
 * The distinction from a 401 matters to whoever reads the error: a 401 means the server
 * rejected us, this means we never asked. Both used to look identical from the UI's side,
 * which is how an authentication failure came to be reported to staff as a failure to load
 * clients.
 */
export class AuthRequiredError extends Error {
  readonly reason: AuthFailureReason;

  constructor(reason: AuthFailureReason, message?: string) {
    super(message ?? `No access token available (${reason}); request not sent.`);
    this.name = 'AuthRequiredError';
    this.reason = reason;
  }
}

/**
 * Whether an unknown thrown value is an auth failure.
 *
 * ⚠ Deliberately **not** `instanceof`. This is checked in render paths against errors that
 * have travelled through react-query, and a duplicated module instance — two copies of the
 * class, one per bundle chunk — makes `instanceof` quietly false. That failure mode restores
 * the exact wrong message this change exists to remove, and it does so with nothing
 * announcing the regression. Matching on `name` survives it. Pinned by a test that feeds
 * this a structurally identical clone built without the class.
 */
export function isAuthRequiredError(error: unknown): error is AuthRequiredError {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { name?: unknown }).name === 'AuthRequiredError'
  );
}

/**
 * Whether an unknown thrown value is the server rejecting us for authentication.
 *
 * Both API modules define their own `ApiError` class, so this reads `status` structurally
 * rather than narrowing to either one — same reasoning as the `name`-based guard above, plus
 * it means a 401 surfaced by any future error type is still recognised as authentication.
 *
 * 📌 Kept separate from {@link isAuthRequiredError} on purpose. *We never asked* and *they
 * said no* are different facts, and collapsing them would hide the case this change is
 * about. {@link isAuthFailure} is the union for surfaces that only need to pick copy.
 */
export function isUnauthorizedResponse(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { status?: unknown }).status === 401
  );
}

/**
 * Whether an error should be reported to staff as an authentication failure — either because
 * no token could be obtained, or because the server refused the one we sent.
 */
export function isAuthFailure(error: unknown): boolean {
  return isAuthRequiredError(error) || isUnauthorizedResponse(error);
}
